import { rpx } from 'client/lib/rpx-client';
import type { State, SetState } from './types';
import { BtnPrimary, Button } from '@components/buttons';
import { BaseDialog, DialogFooter, StandardDialog } from '@components/dialog';
import { showModalForm } from '@components/modal-form';
import { IcoMagnifyingGlass, IcoTrash } from '@components/icons';
import { CalendarForm } from '@components/calendar-form';
import { useAsyncData } from 'client/lib/hooks';
import dayjs from 'dayjs';
import { LoadingIndicator } from '@components/loading-indicator';
import { ExtendedFormRow } from '../calendar-forms/types';

type CalendarForm = {
  id: UUID;
  name: string;
  description: string;
};

type Props = {
  state: State;
  setState: SetState;
};

type ResponsesModaProps = {
  formId: string;
  userId?: string;
  eventId?: string;
  close(): void;
};

function ResponsesModal({ formId, userId, eventId, close }: ResponsesModaProps) {
  const { data: form, isLoading } = useAsyncData(async () => {
    const responses = rpx.ruzcalForms.getFullForm({
      formId: formId,
    });
    return responses;
  }, []);

  return (
    <BaseDialog onClose={close} contentWidth>
      <section class="flex flex-col sm:gap-6 gap-10 sm:p-10 sm:w-2xl lg:w-4xl max-w-screen overflow-auto">
        {isLoading && <LoadingIndicator />}
        {!isLoading && (
          <CalendarForm
            form={{
              id: form?.id ?? '',
              description: form?.description ?? '',
              name: form?.name ?? '',
              questions: form?.questions ?? [],
            }}
            eventId={eventId}
            userId={userId}
            readonly
          />
        )}
      </section>
    </BaseDialog>
  );
}

function SelectedForm({ form, detach }: { form: CalendarForm; detach(): void }) {
  return (
    <section class="border rounded-2xl overflow-hidden">
      <span class="flex flex-col p-4 gap-1 hover:bg-gray-100 transition-all text-inherit relative">
        <span class="font-semibold text-base">{form.name}</span>
        <span class="text-base truncate">{form.description}</span>
        <span
          class="cursor-pointer hidden text-inherit sm:inline-flex items-center absolute gap-3 right-6 top-6 border border-gray-300 rounded-full p-6 py-1 hover:border-gray-400 transition-all"
          onClick={detach}
        >
          <IcoTrash class="size-4 stroke-2" />
          Detach
        </span>
      </span>
      <span class="flex items-center border-t">
        <Button
          href=""
          target="_blank"
          class="hover:bg-gray-200 transition-all rounded-none flex items-center gap-2 text-inherit p-2 px-4"
          onClick={() => {
            showModalForm(({ resolve }) => <ResponsesModal formId={form.id} close={resolve} />);
          }}
        >
          <IcoMagnifyingGlass />
          Preview
        </Button>
      </span>
    </section>
  );
}

function SelectForm({
  forms,
  close,
  apply,
}: {
  forms: ExtendedFormRow[];
  close(): void;
  apply(p: CalendarForm): void;
}) {
  return (
    <StandardDialog onClose={close} title="Connect an existing form">
      <div class="flex flex-col border rounded-lg overflow-hidden divide-y">
        {forms.map((p) => (
          <Button
            key={p.id}
            onClick={() => {
              apply(p);
              close();
            }}
            class="flex flex-col hover:bg-gray-100 text-sm cursor-pointer text-left p-4"
          >
            <strong>{p.name}</strong>
          </Button>
        ))}
      </div>
      <DialogFooter hideConfirm onClose={close} />
    </StandardDialog>
  );
}

export function FormTab({ state, setState }: Props) {
  const selectedForm: CalendarForm | undefined = state.formId
    ? state.forms.find((f) => f.id == state.formId)
    : undefined;

  const { data: responses } = useAsyncData(async () => {
    if (state.id) {
      return await rpx.ruzcalForms.getEventTypeFormResponses({
        eventTypeId: state.id,
      });
    }
  }, [state.id]);

  function showSelectFormModal() {
    return showModalForm<CalendarForm>(({ resolve }) => (
      <SelectForm close={resolve} apply={resolve} forms={state.forms}></SelectForm>
    ));
  }

  return (
    <div class="flex flex-col gap-8">
      {state.formId && selectedForm && (
        <SelectedForm
          form={selectedForm}
          detach={() => {
            setState({ ...state, formId: undefined });
          }}
        ></SelectedForm>
      )}

      {!state.formId && (
        <section>
          <p>Attach a form to gather more information from your attendees.</p>
        </section>
      )}
      <section>
        <BtnPrimary
          class="rounded-full px-8 btn-primary relative disabled:cursor-not-allowed"
          onClick={() =>
            showSelectFormModal().then((p) => {
              if (!p || !p.id) {
                return;
              }

              setState({ ...state, formId: p.id });
            })
          }
        >
          Select a form
        </BtnPrimary>
      </section>

      {!!responses?.length && (
        <section>
          <div class="flex flex-col divide-y">
            <h2>Previous form responses</h2>
            {responses?.map((r) => {
              const day = dayjs(r.submittedAt);
              return (
                <section key={r.id} class="flex gap-2 p-4 items-start">
                  <span class="w-32 flex items-center gap-2">
                    <span class="font-medium text-lg rounded-full size-8 flex items-center justify-center hover:bg-gray-200">
                      {day.format('D')}
                    </span>
                    <span>{day.format('MMM, ddd')}</span>
                  </span>
                  <div class="flex flex-col gap-4 md:gap-2 grow">
                    <span class="font-semibold flex flex-wrap gap-2">
                      <Button
                        onClick={() => {
                          showModalForm(({ resolve }) => (
                            <ResponsesModal
                              formId={r.formId}
                              eventId={r.eventId}
                              userId={r.userId}
                              close={resolve}
                            />
                          ));
                        }}
                      >
                        {r.username} has filled in the form {r.name}
                      </Button>
                    </span>
                  </div>
                </section>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
